import React from 'react';
import { ClockIcon, PlusIcon } from 'lucide-react';
import { Booking, Settings } from '../../types/booking';
import { dayIsOpen, toMinutes, toTime } from '../../utils/availability';
import { dayWindow, placeBookings, statusAccent } from '../../utils/agenda';
import { duration, euro, timeRange } from '../../utils/format';
import { StatusBadge, PaymentBadge } from './StatusBadge';
type Props = {
  iso: string;
  bookings: Booking[];
  settings: Settings;
  services: {
    id: string;
    name: string;
  }[];
  onOpenBooking: (booking: Booking) => void;
};
const PX_PER_MIN = 1.5;
export function AgendaDay({
  iso,
  bookings,
  settings,
  services,
  onOpenBooking
}: Props) {
  const {
    start,
    end
  } = dayWindow([iso], settings);
  const height = (end - start) * PX_PER_MIN;
  const hours = settings.hours[new Date(`${iso}T00:00:00`).getDay()];
  const open = dayIsOpen(iso, settings);
  const blocked = settings.blockedDates.includes(iso);
  const dayBookings = bookings.filter(b => b.date === iso && b.status !== 'cancelled');
  const placed = placeBookings(dayBookings);
  const cancelled = bookings.filter(b => b.date === iso && b.status === 'cancelled');
  const hourMarks: number[] = [];
  for (let m = Math.ceil(start / 60) * 60; m <= end; m += 60) hourMarks.push(m);
  const gaps: {
    from: number;
    to: number;
  }[] = [];
  if (open && hours.open && hours.close && !blocked) {
    const sorted = [...placed].sort((a, b) => a.start - b.start);
    let cursor = toMinutes(hours.open);
    const closeAt = toMinutes(hours.close);
    sorted.forEach(item => {
      if (item.start > cursor) gaps.push({
        from: cursor,
        to: item.start
      });
      cursor = Math.max(cursor, item.end);
    });
    if (cursor < closeAt) gaps.push({
      from: cursor,
      to: closeAt
    });
  }
  return <div className="grid grid-cols-1 gap-5 xl:grid-cols-[minmax(0,1fr)_minmax(0,330px)]" data-id="element-82" data-mp="2evsnabathhai">
      <div className="overflow-hidden rounded-2xl border border-edge bg-carbon" data-id="element-83" data-mp="2jwq4olo067ay">
        <div className="flex items-center justify-between gap-3 border-b border-edge px-5 py-3.5" data-id="element-84" data-mp="2ny1vuvmiz0l9">
          <h2 className="text-xs uppercase tracking-[0.2em] text-dim" data-id="element-85" data-mp="otbkijxgctlm">Dagplanning</h2>
          <span className="text-xs text-dim" data-id="element-86" data-mp="jtylz1x7xy37">
            {open && !blocked ? `${hours.open} — ${hours.close}` : blocked ? 'Geblokkeerd' : 'Gesloten'}
          </span>
        </div>

        <div className="flex px-4 py-4" data-id="element-87" data-mp="su3ujfe5f2iy">
          <div className="relative w-14 shrink-0" style={{
          height
        }} data-id="element-88" data-mp="st286eromtlx">
            {hourMarks.map(mark => <span key={mark} className="absolute right-3 -translate-y-1/2 text-[11px] text-dim" style={{
            top: (mark - start) * PX_PER_MIN
          }} data-id="element-89" data-mp="17h6tlqi4dj8e">
                {toTime(mark)}
              </span>)}
          </div>

          <div className="relative flex-1 rounded-xl border border-edge bg-panel" style={{
          height
        }} data-id="element-90" data-mp="1cj5san2wzrz0">
            {hourMarks.map(mark => <span key={mark} className="absolute inset-x-0 border-t border-edge/70" style={{
            top: (mark - start) * PX_PER_MIN
          }} aria-hidden="true" data-id="element-91" data-mp="3e97kunxdfjsc" />)}

            {(!open || blocked) && <span className={`absolute inset-0 rounded-xl ${blocked ? 'bg-ember/[0.07]' : 'bg-void/50'}`} aria-hidden="true" data-id="element-92" data-mp="388yys657i8tm" />}

            {gaps.map(gap => <span key={`${gap.from}-${gap.to}`} className="absolute inset-x-2 flex items-center justify-center rounded-lg border border-dashed border-edge text-[11px] text-dim/70" style={{
            top: (gap.from - start) * PX_PER_MIN + 2,
            height: Math.max((gap.to - gap.from) * PX_PER_MIN - 4, 18)
          }} data-id="element-93" data-mp="2khjnvozd6j6r">
                {gap.to - gap.from >= 60 && `Vrij · ${duration(gap.to - gap.from)}`}
              </span>)}

            {placed.map(({
            booking,
            start: s,
            lane,
            lanes
          }) => {
            const service = services.find(x => x.id === booking.serviceId);
            return <button key={booking.id} type="button" onClick={() => onOpenBooking(booking)} className={`absolute overflow-hidden rounded-lg border px-2.5 py-1.5 text-left transition-colors duration-200 ease-smooth hover:border-ember ${statusAccent(booking)}`} style={{
              top: (s - start) * PX_PER_MIN + 2,
              height: Math.max(booking.durationMinutes * PX_PER_MIN - 4, 30),
              left: `calc(${lane / lanes * 100}% + 4px)`,
              width: `calc(${100 / lanes}% - 8px)`
            }} data-id="element-94" data-mp="25rvd99ipclyg">
                  <span className="block text-xs font-semibold" data-id="element-95" data-mp="192mbhwxv6uvt">
                    {timeRange(booking.time, booking.durationMinutes)}
                  </span>
                  <span className="block truncate text-sm font-medium" data-id="element-96" data-mp="14549yrm6fhxu">{booking.customer.name}</span>
                  <span className="block truncate text-[11px] opacity-80" data-id="element-97" data-mp="3fspogyxe2ox1">
                    {service?.name} · {booking.customer.car}
                  </span>
                </button>;
          })}
          </div>
        </div>
      </div>

      <div className="space-y-4" data-id="element-98" data-mp="3tp37wdlhh0e5">
        <section className="rounded-2xl border border-edge bg-carbon" data-id="element-99" data-mp="3to1ui2gzw1t9">
          <h2 className="border-b border-edge px-5 py-3.5 text-xs uppercase tracking-[0.2em] text-dim" data-id="element-100" data-mp="2x16lgs3d7av0">
            Afspraken ({dayBookings.length})
          </h2>
          {dayBookings.length > 0 ? <ul className="divide-y divide-edge" data-id="element-101" data-mp="35zs4eocr0rwb">
              {[...dayBookings].sort((a, b) => toMinutes(a.time) - toMinutes(b.time)).map(booking => <li key={booking.id} data-id="element-102" data-mp="2db4lv0jdwgmg">
                    <button type="button" onClick={() => onOpenBooking(booking)} className="w-full px-5 py-4 text-left transition-colors duration-200 ease-smooth hover:bg-panel" data-id="element-103" data-mp="2da1vctpz4g72">
                      <span className="flex items-center justify-between gap-3" data-id="element-104" data-mp="3gmacqzcpg1x3">
                        <span className="truncate font-display text-sm font-extrabold text-ember" data-id="element-105" data-mp="3ud5e7qkvjgqn">
                          {timeRange(booking.time, booking.durationMinutes)}
                        </span>
                        <span className="font-display text-sm font-extrabold text-chalk" data-id="element-106" data-mp="32ntwohsycz2a">
                          {euro(booking.amount)}
                        </span>
                      </span>
                      <span className="mt-1 block truncate font-medium text-chalk" data-id="element-107" data-mp="3boib3dm9d1fp">
                        {booking.customer.name}
                      </span>
                      <span className="mt-2 flex flex-wrap gap-2" data-id="element-108" data-mp="qx839few24r9">
                        <StatusBadge status={booking.status} data-id="element-109" data-mp="qzkrgab8pbcr" />
                        <PaymentBadge status={booking.paymentStatus} data-id="element-110" data-mp="lxddjf6unl7s" />
                      </span>
                    </button>
                  </li>)}
            </ul> : <p className="px-5 py-8 text-sm text-dim" data-id="element-111" data-mp="yi23ooai349f">Geen afspraken op deze dag.</p>}
        </section>

        <section className="rounded-2xl border border-edge bg-carbon p-5" data-id="element-112" data-mp="kisq7wdoio95">
          <h2 className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-dim" data-id="element-113" data-mp="pfj7uo54trwo">
            <ClockIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-114" data-mp="1d5n94ouulfki" />
            Vrije ruimte
          </h2>
          {gaps.length > 0 ? <ul className="mt-4 space-y-2 text-sm" data-id="element-115" data-mp="1i46t4jpvsha8">
              {gaps.map(gap => <li key={`list-${gap.from}`} className="flex items-center justify-between gap-3 rounded-xl border border-edge bg-panel px-4 py-2.5" data-id="element-116" data-mp="13fkfqnjtdzln">
                  <span className="text-chalk" data-id="element-117" data-mp="1w3gb9oxndlgu">
                    {toTime(gap.from)} — {toTime(gap.to)}
                  </span>
                  <span className="text-xs text-dim" data-id="element-118" data-mp="t87p6l78fg0">{duration(gap.to - gap.from)}</span>
                </li>)}
            </ul> : <p className="mt-4 text-sm text-dim" data-id="element-119" data-mp="3s6con9cweqe3">
              {open && !blocked ? 'Volgeboekt — geen ruimte meer vandaag.' : 'De werkplaats is deze dag dicht.'}
            </p>}
        </section>

        {cancelled.length > 0 && <section className="rounded-2xl border border-edge bg-carbon p-5" data-id="element-120" data-mp="js3zkb9l4hqo">
            <h2 className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-dim" data-id="element-121" data-mp="jpzye132lwu3">
              <PlusIcon className="h-3.5 w-3.5 rotate-45" aria-hidden="true" data-id="element-122" data-mp="eqwsyfj6holl" />
              Geannuleerd ({cancelled.length})
            </h2>
            <ul className="mt-4 space-y-2 text-sm" data-id="element-123" data-mp="5sup7s828hum">
              {cancelled.map(booking => <li key={booking.id} data-id="element-124" data-mp="2vi6g4bcuvduj">
                  <button type="button" onClick={() => onOpenBooking(booking)} className="flex w-full items-center justify-between gap-3 rounded-xl border border-edge bg-panel px-4 py-2.5 text-left text-dim transition-colors duration-200 ease-smooth hover:text-chalk" data-id="element-125" data-mp="2vj7y6zsah0lr">
                    <span className="truncate line-through" data-id="element-126" data-mp="1f1fyacumf8an">{booking.customer.name}</span>
                    <span className="text-xs" data-id="element-127" data-mp="1jrwh6ch7nm4e">{booking.time}</span>
                  </button>
                </li>)}
            </ul>
          </section>}
      </div>
    </div>;
}